import { MultipleSelectComponent } from 'src/app/components/multiple-select/multiple-select.component';
import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import {
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import { ICreatedInstanceId, IQueryCacheService } from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardEngagement } from '../sql-maps/bivz-engagement-sessions-with-dimensions-sql-map';

export interface EngagementSessionsDistributionByDaysDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    /** 0.5min or 5min */
    granularity: '0.5min' | '5min';

    gcpProjectId: GcpProjectId | (() => GcpProjectId);
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class EngagementSessionsDistributionByDaysDataLoaderFactory extends Factory<
    DataLoader,
    EngagementSessionsDistributionByDaysDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'EngagementSessionsDistributionByDaysDataLoaderFactory';

    getInstance(
        options: EngagementSessionsDistributionByDaysDataLoaderFactoryOptions
    ) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardEngagement.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                // const maxDayNo =
                //     queryContext.settings[
                //         QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO
                //     ] ?? 30;
                // const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                //     where: [`day_no <= ${maxDayNo}`],
                // };

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';
                const selectedDaysAndSessions =
                    queryContext.settings[
                        QueryContextSettingID
                            .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                    ] ??
                    QueryContextDefaultSettings[
                        QueryContextSettingID
                            .ENGAGEMENT_SELECTED_DAY_AND_SESSION
                    ]!;

                const selectedDaysAndSessionsSQL: Partial<ICompiledSQLTokens> =
                    {
                        where: [
                            MultipleSelectComponent.toNumberSQL('day_no', {
                                value: {
                                    options: selectedDaysAndSessions,
                                    filterType: 'include',
                                },
                                isFilterApplied: true,
                                isSplitApplied: true,
                            }),
                        ],
                    };

                /** ie `3 AS d3, 7 AS d7, 30 AS d30`*/
                const pivotColumns = selectedDaysAndSessions
                    .map((d) => `${d} AS d${d}`)
                    .join(',');

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardEngagement.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    { gcpProjectId: resolvedGcpProjectId },
                    // add custom selectedDaysAndSessionsSQL to compileSQLStatements
                    selectedDaysAndSessionsSQL
                );

                const sessionLengthSQL =
                    options.granularity === '0.5min'
                        ? 'FLOOR(duration_minutes*2)/2'
                        : 'FLOOR(duration_minutes / 5) * 5';
                const bucketLengthSQL =
                    options.granularity === '0.5min' ? 0.5 : 5;
                const sessionLengthLimitSQL =
                    options.granularity === '0.5min' ? '10' : '120';

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH base AS 
(
    SELECT ${splitGroupBy}day_no, session_length
    , FORMAT("%s-%s", CAST(session_length AS STRING), CAST(session_length + ${bucketLengthSQL} AS STRING)) AS session_length_bucket
      , COUNT(*) AS sample
    FROM
    (
      SELECT ${splitSelect}day_no, duration_minutes
        , ${sessionLengthSQL} AS session_length
      FROM \`${generateTableFullPath(
          resolvedGcpProjectId,
          datasourceSqlMap.dataset,
          datasourceSqlMap.tableId
      )}\` ds
    ${join}
    ${where}
    )
    WHERE session_length < ${sessionLengthLimitSQL}
    GROUP BY ${splitGroupBy}day_no, session_length
)

SELECT *
FROM base
PIVOT (ANY_VALUE(sample) FOR day_no IN (${pivotColumns}))
ORDER BY ${splitGroupBy}session_length`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
